import { Component, Input, Output, OnChanges, OnDestroy, SimpleChanges, EventEmitter } from '@angular/core';
import { Product } from '~shared/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import {
  ProductClickApplicationEvent,
  SelectItemApplicationEventModel,
  ViewItemListApplicationEventModel,
} from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '~app/modules/generic/constants';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { Params } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { TRACKED_PRODUCT_INDEX_BASE } from '~app/modules/catalog/constants';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { UrlUtil } from '~shared/utils/url.util';
import { CurrencyService } from '~shared/services/currency.service';
import { NUMBER_10 } from '~shared/constants';

@Component({
  selector: 'app-brand-plp-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
})
export class ProductListComponent implements OnChanges, OnDestroy {
  destroySubject$ = new Subject();
  priceType$ = new BehaviorSubject<PriceTypes>(PriceTypes.b2b);
  public page = 1;
  public limit = DEFAULT_LIMIT;
  currentUrl = this.navigationHelper.getCurrentUrl();
  pageType = UrlUtil.getPageTypeFromCurrentUrl(this.currentUrl);
  isBlackDealsEnabled = this.blackDealsFeatureFlagService.isBlackDealsEnabled();

  @Input() products: Product[] | null = null;
  @Input() isDesktop: boolean;
  @Input() totalCount: number;
  @Input() queryParams: Params;

  @Output() productVisibilityChange = new EventEmitter<number>();

  constructor(
    private applicationEventService: ApplicationEventService,
    private priceTypeService: PriceTypeService,
    private productsListUrlService: ProductsListUrlService,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private navigationHelper: NavigationHelper,
    private currencyService: CurrencyService
  ) {
    this.priceTypeService.priceType$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((priceType: PriceTypes) => this.priceType$.next(priceType));
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.trackGAIfNeeded(changes);
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  onProductClick(product: Product, priceType: PriceTypes, productIndex: number): void {
    const pageOffset = (this.page - 1) * this.limit + productIndex;
    this.publishProductClickEvent(product, priceType, pageOffset);
    this.publishSelectItemEvent(product, priceType, pageOffset);
  }

  onProductVisibility(isVisible: boolean, productIndex: number): void {
    if (this.isDesktop || this.isNotDesiredProductCount(productIndex) || !isVisible) {
      return;
    }

    this.productVisibilityChange.emit(productIndex + 1);
  }

  private isNotDesiredProductCount(productIndex: number): boolean {
    return productIndex !== 0 && (productIndex + 1) % TRACKED_PRODUCT_INDEX_BASE !== 0;
  }

  private trackGAIfNeeded(changes: SimpleChanges): void {
    if (changes['queryParams']) {
      this.updatePage();
    }

    if (this.products === null) {
      //We don't have to fire anything unless all inputs are valid
      return;
    }
  }

  private publishProductClickEvent(product: Product, priceType: PriceTypes, pageOffset: number): void {
    const searchTerm = this.productsListUrlService.getSearchTerm();
    this.applicationEventService.publish(
      new ProductClickApplicationEvent(
        this.currentUrl,
        product,
        priceType,
        this.pageType,
        this.isBlackDealsEnabled,
        pageOffset,
        searchTerm
      )
    );
  }

  private publishSelectItemEvent(product: Product, priceType: PriceTypes, pageOffset: number): void {
    const searchTerm: string = this.productsListUrlService.getSearchTerm();
    this.applicationEventService.publish(
      new SelectItemApplicationEventModel(this.pageType, product, priceType, pageOffset, false, '', '', searchTerm)
    );
  }

  onIndexPower10ProductVisible(index: number): void {
    const slicedProducts: Product[] = this.products.slice(index, index + NUMBER_10);
    const startingIndex: number = (+this.queryParams?.offset || DEFAULT_OFFSET) + index;
    const searchTerm: string = this.productsListUrlService.getSearchTerm();

    this.applicationEventService.publish(
      new ViewItemListApplicationEventModel(
        this.pageType,
        slicedProducts,
        this.priceTypeService.getPriceTypeSync(),
        this.currencyService.getCurrency(),
        startingIndex,
        searchTerm,
        true
      )
    );
  }

  private updatePage(): void {
    const offset = +this.queryParams.offset || DEFAULT_OFFSET;
    const limits = +this.queryParams.limit || this.limit;

    this.page = UrlUtils.getCurrentPage(offset, limits);
  }
}
